import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTicketStore } from '../store/ticketStore'
import { ArrowLeft, Send, Flag, Zap } from 'lucide-react'

export default function TicketDetailPage() {
  const { ticketId } = useParams()
  const navigate = useNavigate()
  const getTicket = useTicketStore((state) => state.getTicket)
  const selectTicket = useTicketStore((state) => state.selectTicket)
  const addMessage = useTicketStore((state) => state.addMessage)
  const updateTicket = useTicketStore((state) => state.updateTicket)
  const selectedTicket = useTicketStore((state) => state.selectedTicket)

  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    selectTicket(ticketId)
  }, [ticketId, selectTicket])

  const ticket = selectedTicket || getTicket(ticketId)

  if (!ticket) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-blue-300/70">Ticket not found</p>
      </div>
    )
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!message.trim()) return

    setLoading(true)
    try {
      await addMessage(ticketId, message)
      setMessage('')
    } catch (err) {
      console.error('Failed to send message:', err)
    } finally {
      setLoading(false)
    }
  }

  const statusColors = {
    open: 'badge-error',
    in_progress: 'badge-warning',
    resolved: 'badge-success',
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/tickets')}
          className="p-2 hover:bg-slate-700/20 rounded-lg transition-colors"
        >
          <ArrowLeft size={24} className="text-blue-200" />
        </button>
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h1 className="text-2xl font-bold text-white">{ticket.id}</h1>
            <span className={`text-xs font-medium px-3 py-1 rounded-full ${statusColors[ticket.status]}`}>
              {ticket.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          <h2 className="text-lg font-semibold text-white">{ticket.title}</h2>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat Area */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="card">
            <div className="card-body">
              <h3 className="font-semibold text-white mb-2">Description</h3>
              <p className="text-blue-200">{ticket.description}</p>
            </div>
          </div>

          {/* Messages */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Conversation</h3>
            </div>
            <div className="card-body space-y-4 max-h-96 overflow-y-auto">
              {ticket.messages.length > 0 ? (
                ticket.messages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'customer' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg ${
                      msg.role === 'customer'
                        ? 'bg-slate-700/20 text-white'
                        : 'bg-cyber-100 text-cyber-900'
                    }`}>
                      <p className="text-sm">{msg.text}</p>
                      <p className={`text-xs mt-1 ${msg.role === 'customer' ? 'text-blue-300/70' : 'text-cyber-700'}`}>
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-blue-300/70 text-center py-8">No messages yet</p>
              )}
            </div>

            {/* Message Input */}
            <div className="border-t border-blue-500/30 p-4">
              <form onSubmit={handleSendMessage} className="flex space-x-2">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="input-base flex-1"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !message.trim()}
                  className="btn-primary px-4"
                >
                  <Send size={20} />
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Info */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Customer</h3>
            </div>
            <div className="card-body space-y-3">
              <div>
                <p className="text-sm text-blue-200">Name</p>
                <p className="font-medium text-white">{ticket.customer.name}</p>
              </div>
              <div>
                <p className="text-sm text-blue-200">Email</p>
                <p className="font-medium text-white">{ticket.customer.email}</p>
              </div>
              <div>
                <p className="text-sm text-blue-200">Phone</p>
                <p className="font-medium text-white">{ticket.customer.phone}</p>
              </div>
            </div>
          </div>

          {/* Ticket Info */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Details</h3>
            </div>
            <div className="card-body space-y-3">
              <div>
                <p className="text-sm text-blue-200">Priority</p>
                <p className={`font-medium uppercase text-sm ${
                  ticket.priority === 'high' ? 'text-red-600' :
                  ticket.priority === 'medium' ? 'text-yellow-600' :
                  'text-green-600'
                }`}>{ticket.priority}</p>
              </div>
              <div>
                <p className="text-sm text-blue-200">Channel</p>
                <p className="font-medium text-white capitalize">{ticket.channel}</p>
              </div>
              <div>
                <p className="text-sm text-blue-200">Assigned To</p>
                <p className="font-medium text-white">{ticket.assignedTo?.name || 'Unassigned'}</p>
              </div>
              <div>
                <p className="text-sm text-blue-200">Created</p>
                <p className="font-medium text-white">{new Date(ticket.createdAt).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Actions</h3>
            </div>
            <div className="card-body space-y-2">
              <button
                onClick={() => updateTicket(ticketId, { status: 'in_progress' })}
                className="btn-secondary w-full justify-center"
              >
                Mark In Progress
              </button>
              <button
                onClick={() => updateTicket(ticketId, { status: 'resolved' })}
                className="btn-primary w-full justify-center"
              >
                Resolve Ticket
              </button>
              <button className="btn-secondary w-full justify-center flex items-center space-x-2">
                <Flag size={18} />
                <span>Escalate</span>
              </button>
              <button className="btn-secondary w-full justify-center flex items-center space-x-2">
                <Zap size={18} />
                <span>Get AI Help</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
